import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  ArrowLeft, PenTool, Type, Calendar, CheckSquare, Baseline,
  MousePointer2, Save, Trash2, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Switch } from '@/components/ui/switch.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { getTemplate, saveTemplateFields, fetchSourcePdf } from '@/services/sign.service.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

// Field-mapping builder — the core unlock. Maps signature/initials/text/date/
// checkbox fields onto a source PDF as resolution-independent fractional rects
// {x_frac, y_frac, w_frac, h_frac} (0..1, y from TOP). At render time the
// overlay engine converts these to PDF points with a y-flip — so the same map
// works on any page size, and on flat scans that have no AcroForm.

// type → palette entry. color is the rect tint; defaults are fractions of page.
const FIELD_TYPES = {
  signature: { label: 'Signature', Icon: PenTool, color: '#c62d48', dw: 0.24, dh: 0.055 },
  initials:  { label: 'Initials',  Icon: Baseline, color: '#7c3aed', dw: 0.09, dh: 0.045 },
  text:      { label: 'Text',      Icon: Type,     color: '#2563eb', dw: 0.22, dh: 0.032 },
  date:      { label: 'Date',      Icon: Calendar, color: '#0891b2', dw: 0.13, dh: 0.032 },
  checkbox:  { label: 'Checkbox',  Icon: CheckSquare, color: '#16a34a', dw: 0.03, dh: 0.03 },
};
const ROLE_SUGGESTIONS = ['signer', 'co-signer', 'broker', 'witness'];
const clamp01 = (n) => Math.min(1, Math.max(0, n));
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `f_${Date.now()}_${Math.round(Math.random() * 1e6)}`);

export default function SignTemplateBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tool, setTool] = useState('select'); // 'select' | a FIELD_TYPES key
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  // Load template + render PDF.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { template } = await getTemplate(id);
        if (cancelled) return;
        setTemplate(template);
        setFields((template.fields || []).map((f) => ({
          _id: uid(),
          field_type: f.field_type, page_index: f.page_index,
          x_frac: f.x_frac, y_frac: f.y_frac, w_frac: f.w_frac, h_frac: f.h_frac,
          signer_role: f.signer_role || 'signer', prefill_key: f.prefill_key || '',
          label: f.label || '', required: f.required !== false, font_size: f.font_size || 11,
        })));
        if (!template.source_url) throw new Error('Template has no source PDF');
        const buf = await fetchSourcePdf(template.source_url);
        if (cancelled) return;
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        if (!cancelled) setPdfDoc(doc);
      } catch (e) {
        if (!cancelled) setLoadErr(e);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const selected = fields.find((f) => f._id === selectedId) || null;

  const addField = useCallback((type, pageIndex, x_frac, y_frac, w_frac, h_frac) => {
    const def = FIELD_TYPES[type];
    const f = {
      _id: uid(), field_type: type, page_index: pageIndex,
      x_frac: clamp01(x_frac), y_frac: clamp01(y_frac),
      w_frac: w_frac ?? def.dw, h_frac: h_frac ?? def.dh,
      signer_role: 'signer', prefill_key: '', label: '', required: true, font_size: 11,
    };
    setFields((prev) => [...prev, f]);
    setSelectedId(f._id);
    setTool('select');
    setDirty(true);
  }, []);

  const patchField = useCallback((id, patch) => {
    setFields((prev) => prev.map((f) => (f._id === id ? { ...f, ...patch } : f)));
    setDirty(true);
  }, []);

  const removeField = useCallback((id) => {
    setFields((prev) => prev.filter((f) => f._id !== id));
    setSelectedId((s) => (s === id ? null : s));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const payload = fields.map((f, i) => ({
        field_type: f.field_type, page_index: f.page_index,
        x_frac: f.x_frac, y_frac: f.y_frac, w_frac: f.w_frac, h_frac: f.h_frac,
        signer_role: f.signer_role || 'signer', prefill_key: f.prefill_key || null,
        label: f.label || null, required: !!f.required, font_size: f.font_size || 11,
        sort_order: i,
      }));
      await saveTemplateFields(id, payload);
      setDirty(false);
      toast.success(`Saved ${payload.length} field${payload.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.body?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [fields, id]);

  // Cmd/Ctrl+S to save; Delete/Backspace to remove the selected field.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); save(); return; }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId
          && !/input|textarea/i.test(e.target.tagName)) {
        e.preventDefault(); removeField(selectedId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, removeField, selectedId]);

  if (loadErr) {
    return (
      <div className="p-4">
        <BackLink navigate={navigate} />
        <div className="mt-4 rounded-card border border-accent/40 bg-accent-soft p-4 text-sm text-accent">
          Couldn't open template: {loadErr.body?.error || loadErr.message}
        </div>
      </div>
    );
  }

  const pageCount = Array.isArray(template?.page_sizes) ? template.page_sizes.length : 0;

  return (
    <div className="flex h-[calc(100vh-3.25rem)] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-3">
          <BackLink navigate={navigate} />
          <div>
            <div className="text-sm font-semibold text-text">{template?.name || 'Template'}</div>
            <div className="text-xs text-text-dim">
              {template?.doc_type ? `${template.doc_type} · ` : ''}{pageCount} page{pageCount === 1 ? '' : 's'} · {fields.length} field{fields.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        <Button size="sm" onClick={save} disabled={saving || !dirty}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {dirty ? 'Save' : 'Saved'}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Palette */}
        <div className="w-44 shrink-0 border-r border-border p-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Tools</div>
          <ToolButton active={tool === 'select'} onClick={() => setTool('select')} Icon={MousePointer2} label="Select" />
          <div className="my-2 h-px bg-border" />
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Fields</div>
          {Object.entries(FIELD_TYPES).map(([key, def]) => (
            <ToolButton key={key} active={tool === key} onClick={() => setTool(key)}
              Icon={def.Icon} label={def.label} color={def.color} />
          ))}
          <p className="mt-3 text-[11px] leading-snug text-text-dim">
            Pick a field, then click or drag on the page to place it. Switch to Select to move, resize, or edit.
          </p>
        </div>

        {/* Canvas */}
        <div className="min-w-0 flex-1 overflow-auto bg-surface-strong/30 p-6">
          {!pdfDoc && (
            <div className="flex h-40 items-center justify-center gap-2 text-sm text-text-muted">
              <Loader2 size={16} className="animate-spin" /> Rendering PDF…
            </div>
          )}
          {pdfDoc && Array.from({ length: pageCount }, (_, i) => (
            <PdfPage
              key={i} pdfDoc={pdfDoc} pageIndex={i} tool={tool}
              fields={fields.filter((f) => f.page_index === i)}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onPlace={(x, y, w, h) => addField(tool, i, x, y, w, h)}
              onPatch={patchField}
            />
          ))}
        </div>

        {/* Properties */}
        <div className="w-72 shrink-0 border-l border-border p-3">
          {!selected && (
            <div className="text-sm text-text-muted">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Properties</div>
              Select a field to edit its role, prefill key, and options.
            </div>
          )}
          {selected && (
            <PropertyPanel field={selected} onPatch={patchField} onRemove={removeField} />
          )}
        </div>
      </div>
    </div>
  );
}

function BackLink({ navigate }) {
  return (
    <button onClick={() => navigate('/admin/sign')}
      className="flex items-center gap-1 text-sm text-text-muted hover:text-text">
      <ArrowLeft size={15} /> Templates
    </button>
  );
}

function ToolButton({ active, onClick, Icon, label, color }) {
  return (
    <button type="button" onClick={onClick}
      className={['mb-1 flex w-full items-center gap-2 rounded-card px-2.5 py-2 text-sm',
        active ? 'bg-accent-soft text-accent' : 'text-text-muted hover:bg-border/30'].join(' ')}>
      <Icon size={15} strokeWidth={1.5} style={color ? { color } : undefined} />
      <span>{label}</span>
    </button>
  );
}

// One rendered PDF page + its field overlay. All placement math is fractional,
// derived from the overlay's displayed bounding rect, so it's independent of
// the render scale.
function PdfPage({ pdfDoc, pageIndex, tool, fields, selectedId, onSelect, onPlace, onPatch }) {
  const canvasRef = useRef(null);
  const layerRef = useRef(null);
  const [dims, setDims] = useState(null);
  const [draft, setDraft] = useState(null); // {x,y,w,h} fractions, while drawing
  const drag = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let task;
    (async () => {
      const page = await pdfDoc.getPage(pageIndex + 1);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const RENDER_W = 850;
      const viewport = page.getViewport({ scale: RENDER_W / base.width });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      // Hold the render task so a re-run (StrictMode double-mount, page change)
      // cancels the in-flight paint instead of reusing the canvas mid-render.
      task = page.render({ canvasContext: ctx, viewport });
      try { await task.promise; } catch (e) { if (e?.name !== 'RenderingCancelledException') throw e; }
      if (!cancelled) setDims({ w: viewport.width, h: viewport.height });
    })();
    return () => { cancelled = true; task?.cancel?.(); };
  }, [pdfDoc, pageIndex]);

  const fracAt = (clientX, clientY) => {
    const r = layerRef.current.getBoundingClientRect();
    return { fx: clamp01((clientX - r.left) / r.width), fy: clamp01((clientY - r.top) / r.height) };
  };

  // Begin an interaction: place (tool=field), or move/resize (tool=select).
  const onPointerDown = (e, field, mode) => {
    e.stopPropagation();
    const { fx, fy } = fracAt(e.clientX, e.clientY);
    if (tool !== 'select' && !field) {
      drag.current = { kind: 'create', startX: fx, startY: fy };
      setDraft({ x: fx, y: fy, w: 0, h: 0 });
    } else if (field) {
      onSelect(field._id);
      drag.current = { kind: mode || 'move', id: field._id, startX: fx, startY: fy, orig: { ...field } };
    } else {
      onSelect(null);
      return;
    }
    // Window-level listeners track the gesture even if the pointer leaves the
    // page; no setPointerCapture (a synthesized pointerId can throw and abort
    // the gesture before these attach).
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const { fx, fy } = fracAt(e.clientX, e.clientY);
    if (d.kind === 'create') {
      setDraft({ x: Math.min(d.startX, fx), y: Math.min(d.startY, fy), w: Math.abs(fx - d.startX), h: Math.abs(fy - d.startY) });
    } else if (d.kind === 'move') {
      const dx = fx - d.startX, dy = fy - d.startY;
      onPatch(d.id, {
        x_frac: clamp01(d.orig.x_frac + dx), y_frac: clamp01(d.orig.y_frac + dy),
      });
    } else if (d.kind === 'resize') {
      onPatch(d.id, {
        w_frac: clamp01(Math.max(0.015, d.orig.w_frac + (fx - d.startX))),
        h_frac: clamp01(Math.max(0.012, d.orig.h_frac + (fy - d.startY))),
      });
    }
  };

  const onPointerUp = (e) => {
    const d = drag.current;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    drag.current = null;
    if (d?.kind === 'create') {
      const { fx, fy } = fracAt(e.clientX, e.clientY);
      const w = Math.abs(fx - d.startX), h = Math.abs(fy - d.startY);
      // Tiny drag = a click: place a default-sized field anchored at the point.
      if (w < 0.01 || h < 0.008) onPlace(d.startX, d.startY);
      else onPlace(Math.min(d.startX, fx), Math.min(d.startY, fy), w, h);
      setDraft(null);
    }
  };

  const placing = tool !== 'select';

  return (
    <div className="mx-auto mb-6 w-fit">
      <div className="mb-1 text-[11px] text-text-dim">Page {pageIndex + 1}</div>
      <div className="relative shadow-lg ring-1 ring-border" style={{ width: dims?.w, height: dims?.h }}>
        <canvas ref={canvasRef} className="block" />
        <div
          ref={layerRef}
          className="absolute inset-0"
          style={{ cursor: placing ? 'crosshair' : 'default' }}
          onPointerDown={(e) => onPointerDown(e, null)}
        >
          {fields.map((f) => {
            const def = FIELD_TYPES[f.field_type];
            const isSel = f._id === selectedId;
            return (
              <div key={f._id}
                onPointerDown={(e) => onPointerDown(e, f, 'move')}
                className="absolute flex items-center justify-center overflow-hidden rounded-[2px] text-[9px] font-medium"
                style={{
                  left: `${f.x_frac * 100}%`, top: `${f.y_frac * 100}%`,
                  width: `${f.w_frac * 100}%`, height: `${f.h_frac * 100}%`,
                  border: `1.5px solid ${def.color}`,
                  background: `${def.color}22`,
                  color: def.color,
                  outline: isSel ? `2px solid ${def.color}` : 'none',
                  outlineOffset: '1px',
                  cursor: placing ? 'crosshair' : 'move',
                }}>
                <span className="pointer-events-none truncate px-1">{f.label || def.label}</span>
                {isSel && (
                  <span
                    onPointerDown={(e) => onPointerDown(e, f, 'resize')}
                    className="absolute -bottom-1 -right-1 h-3 w-3 cursor-nwse-resize rounded-full border border-white"
                    style={{ background: def.color }}
                  />
                )}
              </div>
            );
          })}
          {draft && (
            <div className="absolute border-2 border-dashed border-accent bg-accent/10"
              style={{ left: `${draft.x * 100}%`, top: `${draft.y * 100}%`, width: `${draft.w * 100}%`, height: `${draft.h * 100}%` }} />
          )}
        </div>
      </div>
    </div>
  );
}

function PropertyPanel({ field, onPatch, onRemove }) {
  const def = FIELD_TYPES[field.field_type];
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-text">
          <def.Icon size={15} style={{ color: def.color }} /> {def.label}
        </div>
        <button onClick={() => onRemove(field._id)}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-accent">
          <Trash2 size={13} /> Delete
        </button>
      </div>

      <Field label="Type">
        <select value={field.field_type} onChange={(e) => onPatch(field._id, { field_type: e.target.value })}
          className="h-9 w-full rounded-card border border-border bg-surface px-2 text-sm text-text">
          {Object.entries(FIELD_TYPES).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
        </select>
      </Field>

      <Field label="Signer role">
        <Input list="sign-roles" value={field.signer_role}
          onChange={(e) => onPatch(field._id, { signer_role: e.target.value })} placeholder="signer" />
        <datalist id="sign-roles">{ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}</datalist>
      </Field>

      <Field label="Prefill key" hint="maps to CRM / manifest data; blank = signer fills">
        <Input value={field.prefill_key} onChange={(e) => onPatch(field._id, { prefill_key: e.target.value })}
          placeholder="account.name, coverage.effective_date…" />
      </Field>

      <Field label="Label">
        <Input value={field.label} onChange={(e) => onPatch(field._id, { label: e.target.value })} placeholder={def.label} />
      </Field>

      {field.field_type !== 'signature' && field.field_type !== 'initials' && field.field_type !== 'checkbox' && (
        <Field label="Font size">
          <Input type="number" min={6} max={32} value={field.font_size}
            onChange={(e) => onPatch(field._id, { font_size: Number(e.target.value) || 11 })} />
        </Field>
      )}

      <div className="mt-3 flex items-center justify-between rounded-card border border-border px-3 py-2">
        <span className="text-sm text-text">Required</span>
        <Switch checked={!!field.required} onCheckedChange={(v) => onPatch(field._id, { required: v })} />
      </div>

      <div className="mt-3 text-[11px] leading-snug text-text-dim">
        Pos {(field.x_frac * 100).toFixed(1)}%, {(field.y_frac * 100).toFixed(1)}% · Size {(field.w_frac * 100).toFixed(1)}% × {(field.h_frac * 100).toFixed(1)}%
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs font-medium text-text-muted">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] leading-snug text-text-dim">{hint}</p>}
    </div>
  );
}
